import { ExpressionSpecification, Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { DatasetStyle } from "@/types/dataset";
import { FeatureDataConfig } from "@/types/map";
import {
  changeColorCustomBreaksLayer,
  changeColorCustomOrdinalLayer,
  generateOrdinalColorExpression,
  type LayerSublayers
} from "@/utils/map";

import { addLabelLayer } from "./label";
import { addRankLabelLayer } from "./rank";

/**
 * Creates and configures a GeoJSON source for linestring data
 * @param map - MapLibre map instance
 * @param sourceId - Unique source identifier
 * @param featureCollection - GeoJSON FeatureCollection with LineString/MultiLineString geometries
 */
export const addLineStringSource = (
  map: Map | null,
  sourceId: string,
  featureCollection: FeatureCollection
): void => {
  if (!map) return;

  const parsedLineStringData: FeatureCollection<Geometry> = {
    ...featureCollection,
    features: featureCollection.features.map((feature, index) => ({
      ...feature,
      properties: {
        ...feature.properties,
        unique_id: feature.properties?.unique_id || index
      }
    }))
  };

  map.addSource(sourceId, {
    type: "geojson",
    data: parsedLineStringData,
    promoteId: "unique_id"
  });
};

/**
 * Shared implementation: adds the selection-highlight line, the colored stroke line,
 * and the label & rank layers. Caller supplies the color expression for the stroke.
 */
const addLineStringLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: Map,
  layer: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  strokeColor: ExpressionSpecification | string
): void => {
  const sourceId = layer.id;
  const strokeLayerId = `${sourceId}-line-stroke`;
  const outlineSelectedLayerId = `${sourceId}-outline-selected`;

  if (map.getLayer(strokeLayerId)) {
    map.removeLayer(strokeLayerId);
  }
  if (map.getLayer(outlineSelectedLayerId)) {
    map.removeLayer(outlineSelectedLayerId);
  }

  const lineWidth = style.stroke?.width ?? style.weight ?? 2;
  const lineOpacity = style.stroke?.opacity ?? style.opacity ?? 1;
  const visibility = style?.visibility || "visible";
  const layout = {
    "line-join": "round" as const,
    "line-cap": "round" as const,
    visibility
  };

  // Selection highlight rendered as a casing beside the stroke (no overlap with the
  // colored line). `line-gap-width` matches the stroke width so the highlight pixels
  // sit purely on either side of the stroke.
  map.addLayer({
    id: outlineSelectedLayerId,
    type: "line",
    source: sourceId,
    layout,
    paint: {
      "line-color": "#FFCF01",
      "line-gap-width": lineWidth,
      "line-width": 3,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        1,
        0
      ]
    }
  });

  map.addLayer({
    id: strokeLayerId,
    type: "line",
    source: sourceId,
    layout,
    paint: {
      "line-color": strokeColor,
      "line-width": lineWidth,
      "line-opacity": lineOpacity
    }
  });

  addLabelLayer(isNewUIDatasetConfigEnabled, map, layer, style);
  addRankLabelLayer(isRankingLabelEnabled, map, layer, style);
};

/**
 * Creates line layers with ordinal color mapping (color by category)
 * @param _isNewUIDatasetConfigEnabled - Flag for new UI dataset config
 * @param _isRankingLabelEnabled - Flag for ranking label
 * @param map - MapLibre map instance
 * @param layer - Layer configuration object
 * @param style - Dataset style configuration
 * @param category - Category attribute name for coloring
 */
export const addLineStringOrdinalLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: Map | null,
  layer: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map) return;

  const colorExpression = generateOrdinalColorExpression(
    style.colorRanges,
    style.colorFamily,
    category,
    style.colorSteps
  );

  addLineStringLayers(
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    layer,
    style,
    colorExpression
  );
};

/**
 * Creates line layers with quantile color mapping (color by value range)
 * @param map - MapLibre map instance
 * @param sourceId - Source identifier
 * @param colorRanges - Array of color ranges with min/max values
 * @param selectedCategory - Attribute name to color by
 * @param opacity - Layer opacity
 * @param width - Line width
 */
export const addLineStringQuantileLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: Map | null,
  layer: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map) return;

  // Sort ranges by min value to ensure correct step expression
  const sortedRanges = [...style.colorRanges].sort((a, b) => {
    return (a.min ?? 0) - (b.min ?? 0);
  });

  // Create step expression
  // ["step", ["get", property], defaultColor, stop1, color1, stop2, color2, ...]
  const stepExpression: ExpressionSpecification = [
    "step",
    ["get", category],
    sortedRanges[0]?.color || "#ccc", // Default color (below first min)
    ...sortedRanges.flatMap((range) => {
      if (range.min !== undefined && range.min !== null) {
        return [range.min, range.color];
      }
      return [];
    })
  ];

  addLineStringLayers(
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    layer,
    style,
    stepExpression
  );
};

/**
 * Line layers with the "Simple" color scale: one flat stroke color for the whole
 * layer (the config's `fillColor`, since lines have no fill layer).
 */
export const addLineStringSimpleLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: Map | null,
  layer: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle
): void => {
  if (!map) return;

  const strokeColor = style.simpleConfig?.fillColor ?? style.fillColor;

  addLineStringLayers(
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    layer,
    style,
    strokeColor
  );
};

/**
 * Line layers with the "Custom Ordinal" color scale: per-value stroke colors from
 * the committed config (`fillColor` drives the stroke). Mirrors the polygon path.
 */
export const addLineStringCustomOrdinalLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: Map | null,
  layer: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map) return;

  addLineStringSimpleLayers(
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    layer,
    style
  );

  if (style.customOrdinalConfig && layer.sublayers) {
    changeColorCustomOrdinalLayer(
      map,
      layer.sublayers as LayerSublayers,
      style.customOrdinalConfig,
      category,
      style.stroke?.opacity ?? style.fillOpacity ?? 0.7
    );
  }
};

/**
 * Line layers with the "Custom Breaks" color scale: per-range stroke colors from
 * the committed config (`fillColor` drives the stroke). Mirrors the polygon path.
 */
export const addLineStringCustomBreaksLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: Map | null,
  layer: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map) return;

  addLineStringSimpleLayers(
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    map,
    layer,
    style
  );

  if (style.customBreaksConfig && layer.sublayers) {
    changeColorCustomBreaksLayer(
      map,
      layer.sublayers as LayerSublayers,
      style.customBreaksConfig,
      category,
      style.stroke?.opacity ?? style.fillOpacity ?? 0.7
    );
  }
};
